import { CloudUploadOutlined } from "@ant-design/icons";
import { FirebaseService, ToastService } from "@shared/lib/services";
import { Button, Form, Image, Input, Spin, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import { useState } from "react";
import { ICreateOrganisationRequest } from "../lib/types";
import styles from "./OrganisationForm.module.scss";

const { Dragger } = Upload;

interface OrganisationFormProps {
  onSubmit: (values: ICreateOrganisationRequest) => void;
  isSubmitting: boolean;
  initialValues?: Partial<ICreateOrganisationRequest>;
  onClose?: () => void;
}

export function OrganisationForm({
  onSubmit,
  isSubmitting,
  initialValues,
  onClose,
}: OrganisationFormProps) {
  const [form] = Form.useForm();
  const [logoUrl, setLogoUrl] = useState(initialValues?.logoUrl || "");
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const handleImageUpload = async (file: RcFile) => {
    try {
      setIsUploading(true);
      const uploadedUrl = await FirebaseService.uploadFileToFirebase(
        "organisation-logos",
        file
      );
      setLogoUrl(uploadedUrl);
      form.setFieldValue("logoUrl", uploadedUrl);
      ToastService.success("Logo uploaded successfully.");
    } catch (error) {
      ToastService.error("Image upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
    return false; // Prevent antd default upload
  };

  const handleRemoveLogo = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLogoUrl("");
    form.setFieldValue("logoUrl", "");
    ToastService.success("Logo removed successfully.");
  };

  const handleSubmit = (values: any) => {
    onSubmit({ ...values, logoUrl });
  };

  return (
    <div className={styles.formContainer}>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        requiredMark={false}
        initialValues={initialValues}
        className={styles.form}
      >
        <div className={styles.formBody}>
          <Form.Item
            name="name"
            label={<span className={styles.label}>Company Name</span>}
            rules={[{ required: true, message: "Company name is required" }]}
          >
            <Input placeholder="Enter company name..." />
          </Form.Item>

          <Form.Item
            label={<span className={styles.label}>Company Logo</span>}
            name="logoUrl"
            rules={[
              { required: true, message: "Organisation logo is required" },
            ]}
          >
            <Dragger
              name="file"
              multiple={false}
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/jpeg,image/jpg,image/png"
              className={styles.dragger}
            >
              <Spin spinning={isUploading}>
                {logoUrl ? (
                  <div className={styles.logoPreview}>
                    <Image
                      src={logoUrl}
                      alt="Organisation Logo"
                      width={80}
                      height={80}
                      style={{
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid white",
                        boxShadow:
                          "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                      }}
                      preview={false}
                    />
                    <p className={styles.logoName}>logo.png</p>
                    <Button
                      type="link"
                      danger
                      onClick={handleRemoveLogo}
                      className={styles.removeButton}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className={styles.uploadPrompt}>
                    <div className={styles.uploadIconWrapper}>
                      <CloudUploadOutlined className={styles.uploadIcon} />
                    </div>
                    <p className={styles.promptText}>
                      Drag & drop or{" "}
                      <span className={styles.promptLink}>click to upload</span>
                    </p>
                    <p className={styles.promptHint}>PNG, JPG, GIF up to 5MB</p>
                  </div>
                )}
              </Spin>
            </Dragger>
          </Form.Item>
        </div>

        <div className={styles.formFooter}>
          <Button onClick={onClose} style={{ marginRight: "8px" }}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isSubmitting}
            disabled={isSubmitting || isUploading}
          >
            {isSubmitting
              ? initialValues
                ? "Updating..."
                : "Adding..."
              : initialValues
                ? "Update Company"
                : "Add Company"}
          </Button>
        </div>
      </Form>
    </div>
  );
}
